import { useState, useEffect } from 'react';
import { Box, Button, CircularProgress, Dialog, Grid, IconButton, Tooltip, Typography, useTheme, } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export interface KraLink {
    name: string;
    url: string;
}

export interface KycInfoPopupProps {
    open: boolean;
    onClose?: () => void;
    title?: string;
    subtitle?: string;
    copyText?: string;
    copyLinks?: KraLink[];
    showSubmitButton?: boolean;
    submitButtonLabel?: string;
    onSubmitBtnClick?: () => void;
    showCloseButton?: boolean;
    closeButtonLabel?: string;
    onCloseBtnClick?: () => void;
    titleSpacing?: number;
    isSubmitBtnLoading?: boolean;
    isCloseBtnLoading?: boolean;
}

const COPIED_DELAY = 1500

export default function KycInfoPopup({
    open,
    onClose = () => { return; },
    title,
    subtitle,
    copyText,
    copyLinks,
    showSubmitButton = true,
    submitButtonLabel = 'Submit',
    onSubmitBtnClick,
    showCloseButton = false,
    closeButtonLabel = 'Close',
    onCloseBtnClick,
    titleSpacing = 2,
    isSubmitBtnLoading = false,
    isCloseBtnLoading = false,
}: KycInfoPopupProps): JSX.Element {
    const theme = useTheme()
    const [copied, setCopied] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

    useEffect(() => {
        if (!copied) return;

        const timer = setTimeout(() => { setCopied(false); setCopiedIndex(null); }, COPIED_DELAY);
        return () => clearTimeout(timer);
    }, [copied]);

    const handleCopy = async (text?: string, index?: number) => {
        const textToCopy = text || copyText;
        if (!textToCopy) return;
        try {
            await navigator.clipboard.writeText(textToCopy);
            setCopied(true);
            if (index !== undefined) setCopiedIndex(index);
        } catch (error) {
            console.log('Failed to copy text:', error);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            PaperProps={{
                sx: { padding: 3 }
            }}
        >
            {title && (
                <Typography
                    variant='h2'
                    sx={{
                        textAlign: 'center',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        mb: titleSpacing,
                    }}
                >
                    {title}
                </Typography>
            )}
            {subtitle && (
                <Typography
                    variant="body_500"
                    sx={{
                        textAlign: 'center',
                        px: 3,
                        pb: 1,
                        mb: 1,
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'pre-line',
                    }}
                >
                    {subtitle}
                </Typography>
            )}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                {/* Multiple KRA links */}
                {copyLinks && copyLinks.length > 0 && (
                    <Box sx={{ width: '100%', mb: 2 }}>
                        {copyLinks.map((link, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    py: 1,
                                    px: 2,
                                    borderBottom: index < copyLinks.length - 1 ? `1px solid ${theme.palette.grey[200]}` : 'none',
                                }}
                            >
                                <Typography variant="body_500" sx={{ color: theme.palette.text.primary }}>
                                    {link.name}
                                </Typography>
                                <Tooltip title={copiedIndex === index ? "Copied!" : "Copy Link"}>
                                    <Button
                                        startIcon={<ContentCopyIcon />}
                                        onClick={() => handleCopy(link.url, index)}
                                        size="small"
                                        sx={{
                                            ...theme.typography.subtitle_600,
                                            color: theme.palette.text.secondary,
                                            fontWeight: 500,
                                            minWidth: 'auto',
                                        }}
                                        disabled={copiedIndex === index}
                                    >
                                        {copiedIndex === index ? 'Copied' : 'Copy Link'}
                                    </Button>
                                </Tooltip>
                            </Box>
                        ))}
                    </Box>
                )}
                {/* Single copy link (backward compatible) */}
                {!copyLinks?.length && copyText && (
                    <Button
                        startIcon={<ContentCopyIcon />}
                        onClick={() => handleCopy()}
                        sx={{
                            ...theme.typography.subtitle_600,
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            padding: "5px",
                            mb: 2,
                        }}
                        disabled={copied}
                    >
                        {copied ? 'Copied' : 'Copy Link'}
                    </Button>
                )}
                {(showSubmitButton && showCloseButton) && (
                    <Grid container spacing={3} sx={{ width: '100%' }}>
                        <Grid item xs={6}>
                            <LoadingButton
                                variant="outlined"
                                onClick={onCloseBtnClick}
                                fullWidth
                                loading={isCloseBtnLoading}
                                disabled={isSubmitBtnLoading}
                                loadingIndicator={
                                    <CircularProgress size={18} sx={{ color: theme.palette.primary[500] }} />
                                }
                                sx={{
                                    ...theme.typography.body_600,
                                    color: theme.palette.text.primary,
                                    padding: '7px'
                                }}
                            >
                                {closeButtonLabel}
                            </LoadingButton>
                        </Grid>
                        <Grid item xs={6}>
                            <LoadingButton
                                variant="contained"
                                onClick={onSubmitBtnClick}
                                fullWidth
                                loading={isSubmitBtnLoading}
                                disabled={isCloseBtnLoading}
                                loadingIndicator={
                                    <CircularProgress size={18} sx={{ color: theme.palette.common.white }} />
                                }
                                sx={{
                                    ...theme.typography.body_600,
                                    color: theme.palette.common.white,
                                    padding: '7px'
                                }}
                            >
                                {submitButtonLabel}
                            </LoadingButton>
                        </Grid>
                    </Grid>
                )}
                {(showSubmitButton && !showCloseButton) && (
                    <Grid container justifyContent="center">
                        <Grid item>
                            <LoadingButton
                                variant="contained"
                                onClick={onSubmitBtnClick}
                                loading={isSubmitBtnLoading}
                                loadingIndicator={
                                    <CircularProgress size={18} sx={{ color: theme.palette.common.white }} />
                                }
                                sx={{
                                    ...theme.typography.body_600,
                                    color: theme.palette.common.white,
                                    padding: '7px 24px',
                                }}
                            >
                                {submitButtonLabel}
                            </LoadingButton>
                        </Grid>
                    </Grid>
                )}
                {(!showSubmitButton && showCloseButton) && (
                    <Grid container justifyContent="center">
                        <Grid item>
                            <LoadingButton
                                variant="outlined"
                                onClick={onCloseBtnClick}
                                loading={isCloseBtnLoading}
                                loadingIndicator={
                                    <CircularProgress size={18} sx={{ color: theme.palette.primary[500] }} />
                                }
                                sx={{
                                    ...theme.typography.body_600,
                                    color: theme.palette.text.primary,
                                    padding: '7px 24px',
                                }}
                            >
                                {closeButtonLabel}
                            </LoadingButton>
                        </Grid>
                    </Grid>
                )}
            </Box>
        </Dialog>
    );
}
